import { Activity, Mail, Phone, SlidersHorizontal, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { api } from "@/api";
import { MySessions } from "@/components/session/MySessions";
import { ThemeStats } from "@/components/theme/ThemeStats";
import { useAuth } from "@/auth";
import { DateField } from "@/components/DateField";
import { PERIODS, TYPE_META, TypeIcon } from "@/components/cockpit/meta";
import { ByUser, Timeline, TouchedList } from "@/components/cockpit/Views";
import { AnomaliesPanel } from "@/components/cockpit/Anomalies";
import { UnattachedCalls } from "@/components/cockpit/Unattached";
import { RingoverSettings } from "@/components/cockpit/RingoverSettings";
import { AiActionsPanel } from "@/components/cockpit/AiActions";
import { DebriefBanner } from "@/components/cockpit/DebriefBanner";
import { BriefingCard, Skeleton } from "@/components/cockpit/BriefingCard";
import { BriefingSettings } from "@/components/cockpit/BriefingSettings";
import { SessionReport } from "@/components/cockpit/SessionReport";
import { StatsPanel } from "@/components/cockpit/StatsPanel";
import { MyTasksToday } from "@/components/cockpit/MyTasksToday";
import "@/cockpit.css";
import "@/session.css";

const COUNTERS = [["fiches", "Fiches touchées"], ["call", "Appels"], ["email", "E-mails"], ["sms", "SMS"], ["comment", "Commentaires"], ["stage_change", "Changements d'étape"]];
const FILTERS = [["user_id", "Commercial", "users"], ["type", "Type", "types"], ["direction", "Sens", null], ["tag", "Tag", "tags"], ["stage", "Étape", "stages"], ["pipeline", "Pipeline", "pipelines"], ["prospect_file", "Fichier", "files"]];
const DIRS = [["inbound", "Entrant"], ["outbound", "Sortant"], ["internal", "Interne"]];

const FilterBar = ({ opts, f, setF }) => {
  const [open, setOpen] = useState(false);
  const active = FILTERS.filter(([key]) => f[key]);
  const name = (key, v) => (key === "type" ? TYPE_META[v]?.label : key === "direction" ? DIRS.find(([d]) => d === v)?.[1] : key === "prospect_file" ? (opts.files || []).find((x) => x.id === v)?.name : v) || v;
  return (
    <div className="ck-filterwrap">
      <div className="ck-filters-top">
        <button type="button" className={`pl-filters-btn${open ? " is-open" : ""}`} onClick={() => setOpen(!open)} aria-expanded={open} data-testid="ck-filters-toggle"><SlidersHorizontal size={16} /> Filtres{active.length > 0 && <b data-testid="ck-filters-count">{active.length}</b>}</button>
        {active.map(([key, label]) => <button key={key} type="button" className="pl-chip" onClick={() => setF({ ...f, [key]: "" })} data-testid={`ck-filter-chip-${key}`}>{label} : {name(key, f[key])} <X size={12} /></button>)}
        {active.length > 0 && <button type="button" className="ck-reset" onClick={() => setF({})} data-testid="ck-filters-reset">Effacer</button>}
      </div>
      {open && <FilterSelects opts={opts} f={f} setF={setF} />}
    </div>
  );
};

const FilterSelects = ({ opts, f, setF }) => (
  <div className="ck-filters pl-fpane" data-testid="ck-filters">
    {FILTERS.map(([key, label, src]) => {
      const list = src === "types" ? Object.entries(TYPE_META).map(([k, m]) => [k, m.label]) : src === "files" ? (opts.files || []).map((x) => [x.id, x.name]) : src ? (opts[src] || []).map((x) => [x, x]) : DIRS;
      return (
        <select key={key} value={f[key] || ""} onChange={(e) => setF({ ...f, [key]: e.target.value })} className={f[key] ? "on" : ""} data-testid={`ck-filter-${key}`}>
          <option value="">{label} : tous</option>
          {list.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      );
    })}
  </div>
);

export default function CockpitPage() {
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const [period, setPeriod] = useState("today");
  const [range, setRange] = useState({ start: "", end: "" });
  const [f, setF] = useState({});
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "fiches");
  const [aiCount, setAiCount] = useState(null);
  const [opts, setOpts] = useState({});
  const [touched, setTouched] = useState(null);
  const [events, setEvents] = useState(null);
  const [anoms, setAnoms] = useState(null);
  const [anomKind, setAnomKind] = useState("");
  const [archived, setArchived] = useState(false);
  const params = useMemo(() => ({ period, ...(period === "custom" ? range : {}), ...(archived ? { archived: true } : {}), ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }), [period, range, f, archived]);
  const load = useCallback(() => {
    if (period === "custom" && !range.start) return;
    setTouched(null);
    api.get("/activities/touched-fiches", { params: { ...params, limit: 500 } }).then(({ data }) => setTouched(data)).catch(() => setTouched({ fiches: [], counters: {}, total: 0 }));
    setAnoms(null);
    api.get("/activities/anomalies", { params: { period, ...(period === "custom" ? range : {}), ...(archived ? { archived: true } : {}) } }).then(({ data }) => setAnoms(data)).catch(() => setAnoms({ total: 0, counts: {}, items: [] }));
  }, [params, period, range, archived]);
  useEffect(() => { load(); }, [load]);
  // La timeline (le plus gros volume) n'est chargée que lorsqu'on ouvre son onglet
  useEffect(() => {
    if (tab !== "timeline" || (period === "custom" && !range.start)) return;
    setEvents(null);
    api.get("/activities", { params: { ...params, limit: 300 } }).then(({ data }) => setEvents(data)).catch(() => setEvents({ items: [], total: 0 }));
  }, [tab, params, period, range]);
  useEffect(() => { api.get("/activities/filter-options").then(({ data }) => setOpts(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/ai-actions", { params: { sort: "date" } }).then(({ data }) => setAiCount(data.pending)).catch(() => {}); }, []);
  return (
    <section className="page ck-page" data-testid="cockpit-page">
      <header className="ck-head">
        <div><p className="eyebrow">COCKPIT D'ACTIVITÉ</p><h1>Cockpit d'activité</h1></div>
        <ThemeStats stats={[
          { icon: Activity, value: touched ? touched.counters.fiches || 0 : "…", label: "Fiches touchées", testid: "ck-head-fiches" },
          { icon: Phone, value: touched ? touched.counters.call || 0 : "…", label: "Appels", testid: "ck-head-calls" },
          { icon: Mail, value: touched ? (touched.counters.email || 0) + (touched.counters.sms || 0) : "…", label: "E-mails et SMS", tone: "pine", testid: "ck-head-messages" },
        ]} />
        {admin && <div className="ck-demo"><RingoverSettings /><BriefingSettings /></div>}
      </header>
      <div className="ck-top-grid" data-testid="ck-top-grid">
        <div className="ck-top-grid__main"><MySessions /><BriefingCard /></div>
        <div className="ck-top-grid__side"><MyTasksToday /><DebriefBanner /></div>
      </div>
      <SessionReport />
      <div className="ck-periods" data-testid="ck-periods">
        {PERIODS.map(([k, l]) => <button key={k} type="button" className={period === k ? "on" : ""} onClick={() => setPeriod(k)} data-testid={`ck-period-${k}`}>{l}</button>)}
        <label className="ck-archived" data-testid="ck-archived-label"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} data-testid="ck-archived-toggle" /> Inclure les pipelines archivés</label>
        {period === "custom" && <span className="ck-range"><DateField value={range.start} onChange={(v) => setRange({ ...range, start: v })} clearable={false} testid="ck-range-start" /> → <DateField value={range.end} onChange={(v) => setRange({ ...range, end: v })} clearable={false} testid="ck-range-end" /></span>}
      </div>
      <FilterBar opts={opts} f={f} setF={setF} />
      <div className="ck-counters" data-testid="ck-counters">
        {COUNTERS.map(([k, l]) => (
          <div key={k} className="ck-counter" data-testid={`ck-counter-${k}`}>
            {k !== "fiches" && <TypeIcon type={k} />}
            <strong>{touched ? (touched.counters[k] || 0).toLocaleString("fr-FR") : <span className="ck-skel-num" />}</strong><span>{l}</span>
          </div>
        ))}
      </div>
      <div className="ck-tabs" role="tablist">
        {[["ai", "À valider"], ["stats", "Statistiques"], ["fiches", "Fiches touchées"], ["timeline", "Timeline"], ["users", "Par commercial"], ["anomalies", "Anomalies"], ...(admin ? [["unattached", "Appels non rattachés"]] : [])].map(([k, l]) => <button key={k} type="button" className={`${tab === k ? "on" : ""}${k === "anomalies" ? " warn" : ""}`} onClick={() => setTab(k)} data-testid={`ck-tab-${k}`}>{l}{k === "anomalies" && anoms ? <b className="ck-badge" data-testid="ck-anom-count">{anoms.total}</b> : null}{k === "ai" && aiCount ? <b className="ck-badge ai" data-testid="ck-ai-count">{aiCount}</b> : null}</button>)}
      </div>
      {tab === "ai" ? <AiActionsPanel onCount={setAiCount} /> : tab === "stats" ? <StatsPanel params={params} /> : tab === "anomalies" ? <AnomaliesPanel data={anoms} kind={anomKind} setKind={setAnomKind} /> : tab === "unattached" ? <UnattachedCalls period={period} range={range} onChanged={load} /> : !touched ? <Skeleton lines={8} testid="ck-list-loading" /> : !touched.fiches.length && tab !== "timeline" ? <p className="ck-empty" data-testid="ck-empty">Aucune activité sur cette période avec ces filtres.</p> : (
        <>
          {tab === "fiches" && <TouchedList fiches={touched.fiches} />}
          {tab === "users" && <ByUser fiches={touched.fiches} />}
          {tab === "timeline" && (!events ? <Skeleton lines={8} testid="ck-timeline-loading" /> : events.items.length ? <Timeline items={events.items} /> : <p className="ck-empty">Aucun événement.</p>)}
          {tab === "timeline" && events?.total > events?.items?.length && <p className="ck-muted">{events.items.length} événements affichés sur {events.total.toLocaleString("fr-FR")}.</p>}
        </>
      )}
    </section>
  );
}
